import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Input,
  Select,
  Modal,
  Form,
  message,
  Tabs
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import type { ApiResponse } from '@/types';
import {
  useEstimations,
  useCreateEstimation,
  useDeleteEstimation,
  useAccuracyMetrics,
  usePredictCost
} from '../../hooks/useEstimation';
import { useActiveCustomers } from '../../hooks/useCustomers';
import { useCurrency } from '@/contexts/CurrencyContext';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_ESTIMATION } from '@/constants/approval-entity-types';
import { crmEstimationService } from '@/services/crmEstimation.service';
import { canDeleteEstimation } from '../../utils/documentDeletePolicy';

const { Option } = Select;
const { TabPane } = Tabs;

interface CrmEstimationRow {
  id: string;
  estimation_number?: string;
  estimation_name?: string;
  status?: string;
  grand_total?: number;
  total_cost?: number;
  approval_instance_id?: string | null;
}

export const EstimationDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const crmOpportunityId =
    searchParams.get('crm_opportunity_id') ||
    searchParams.get('opportunity_id') ||
    undefined;
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const [form] = Form.useForm();
  const { baseCurrency, formatCurrency } = useCurrency();

  // Queries
  const { data: estimationsData, isLoading, refetch } = useEstimations();

  const { data: crmEstimations = [], isLoading: crmEstLoading } = useQuery({
    queryKey: ['crm-estimations', 'opp', crmOpportunityId],
    queryFn: async () => {
      const res = await api.get<CrmEstimationRow[]>(`/crm/estimations`, {
        params: { opportunity_id: crmOpportunityId },
      });
      const d = (res as ApiResponse<CrmEstimationRow[]>).data;
      return Array.isArray(d) ? d : [];
    },
    enabled: !!crmOpportunityId,
  });
  const { data: accuracyData } = useAccuracyMetrics();
  const { data: customers, isLoading: isLoadingCustomers } = useActiveCustomers();
  const createEstimationMutation = useCreateEstimation();
  const deleteEstimationMutation = useDeleteEstimation();
  const predictCostMutation = usePredictCost();

  // Extract data (API may return array or { data })
  const estimations = Array.isArray(estimationsData)
    ? estimationsData
    : (estimationsData as { data?: unknown[] } | undefined)?.data ?? [];
  const accuracyPack = accuracyData as { data?: Record<string, unknown> } | undefined;
  const accuracyMetrics = (accuracyPack?.data ??
    (accuracyData as Record<string, unknown>) ??
    {}) as Record<string, any>;

  // Calculate statistics
  const totalEstimations = estimations.length;
  const draftEstimations = estimations.filter((e: any) => e.status === 'Draft').length;
  const approvedEstimations = estimations.filter((e: any) => e.status === 'Approved').length;
  const totalValue = estimations.reduce((sum: number, e: any) => sum + (e.total_cost || 0), 0);

  // Handlers
  const handleCreateEstimation = async (values: any) => {
    try {
      const payload = crmOpportunityId
        ? { ...values, opportunity_id: crmOpportunityId }
        : values;
      await createEstimationMutation.mutateAsync(payload);
      message.success('Estimation created successfully');
      setIsCreateModalVisible(false);
      form.resetFields();
      refetch();
    } catch (error) {
      message.error('Failed to create estimation');
    }
  };

  const handleDeleteEstimation = async (id: string) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this estimation?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteEstimationMutation.mutateAsync(id);
          message.success('Estimation deleted successfully');
          refetch();
        } catch (error) {
          message.error('Failed to delete estimation');
        }
      }
    });
  };

  // Table columns
  const columns = [
    {
      title: 'Estimation #',
      dataIndex: 'estimation_number',
      key: 'estimation_number',
      sorter: (a: any, b: any) => a.estimation_number.localeCompare(b.estimation_number)
    },
    {
      title: 'Name',
      dataIndex: 'estimation_name',
      key: 'estimation_name',
      filteredValue: [searchText],
      onFilter: (value: any, record: any) =>
        record.estimation_name.toLowerCase().includes(value.toLowerCase()) ||
        record.customer_name?.toLowerCase().includes(value.toLowerCase())
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name'
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      key: 'department_id',
      render: (departmentId: string | null | undefined) => {
        if (!departmentId) return '—';
        const d = departments.find((x) => x.id === departmentId);
        return d ? `${d.department_code} - ${d.department_name}` : departmentId;
      },
    },
    {
      title: 'Opportunity',
      key: 'opportunity',
      render: (_: unknown, record: any) => {
        const rel = record?.opportunity;
        const number = rel?.opportunity_number;
        const name = rel?.opportunity_name;
        const rawId = record?.opportunity_id;
        if (number || name) return [number, name].filter(Boolean).join(' - ');
        return rawId || '-';
      }
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      render: (value: number) => formatCurrency(value || 0),
      sorter: (a: any, b: any) => a.total_cost - b.total_cost
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      render: (value: number) => formatCurrency(value || 0)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'Approved') color = 'success';
        else if (status === 'Draft') color = 'warning';
        else if (status === 'Rejected') color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Submitted', value: 'Submitted' },
        { text: 'Approved', value: 'Approved' },
        { text: 'Rejected', value: 'Rejected' }
      ],
      onFilter: (value: any, record: any) => record.status === value
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => navigate(`/estimation/${record.id}`)}>
            View
          </Button>
          <Button type="link" size="small" onClick={() => navigate(`/estimation/${record.id}/edit`)}>
            Edit
          </Button>
          <Button type="link" size="small" onClick={() => navigate(`/estimation/multi-item/${record.id}`)}>
            Multi-Item
          </Button>
          <Button type="link" size="small" onClick={() => navigate(`/estimation/${record.id}/versions`)}>
            Versions
          </Button>
          {(() => {
            const deleteCheck = canDeleteEstimation(record);
            return (
              <Button
                type="link"
                danger
                size="small"
                disabled={!deleteCheck.ok}
                title={deleteCheck.ok ? undefined : deleteCheck.reason}
                onClick={() => {
                  if (!deleteCheck.ok) return;
                  handleDeleteEstimation(record.id);
                }}
              >
                Delete
              </Button>
            );
          })()}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>Cost Estimation Dashboard</h1>
        <p style={{ color: '#8c8c8c' }}>
          Manage cost estimations with predictions and comprehensive analysis
        </p>
      </div>

      {crmOpportunityId ? (
        <Card title={`CRM estimations (opportunity ${crmOpportunityId})`} style={{ marginBottom: 24 }} loading={crmEstLoading}>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={crmEstimations}
            expandable={{
              expandedRowRender: (r: CrmEstimationRow) =>
                r.approval_instance_id ? (
                  <div style={{ padding: 12, background: '#fafafa' }}>
                    <ApprovalPanel
                      entityType={ENTITY_TYPE_ESTIMATION}
                      entityId={r.id}
                      onApproveViaDocument={async (remarks) => {
                        await crmEstimationService.approve(r.id, remarks);
                        queryClient.invalidateQueries({ queryKey: ['crm-estimations', 'opp', crmOpportunityId] });
                      }}
                      onRejectViaDocument={async (payload) => {
                        await crmEstimationService.rejectUniversalWorkflow(r.id, payload);
                        queryClient.invalidateQueries({ queryKey: ['crm-estimations', 'opp', crmOpportunityId] });
                      }}
                      extraInvalidateQueryKeys={[['crm-estimations', 'opp', crmOpportunityId]]}
                    />
                  </div>
                ) : null,
              rowExpandable: (r: CrmEstimationRow) => !!r.approval_instance_id,
            }}
            columns={[
              { title: '#', dataIndex: 'estimation_number', key: 'n' },
              { title: 'Name', dataIndex: 'estimation_name', key: 'nm' },
              {
                title: 'Opportunity',
                key: 'opp',
                render: (_: unknown, r: any) => {
                  const num = r?.opportunity?.opportunity_number;
                  const name = r?.opportunity?.opportunity_name;
                  if (num || name) return [num, name].filter(Boolean).join(' - ');
                  return r?.opportunity_id || crmOpportunityId || '-';
                },
              },
              { title: 'Status', dataIndex: 'status', key: 'st' },
              {
                title: 'Total',
                key: 't',
                render: (_: unknown, r: CrmEstimationRow) =>
                  (r.grand_total ?? r.total_cost ?? 0).toLocaleString(),
              },
            ]}
          />
          <Button type="link" onClick={() => navigate('/growth/estimations')} style={{ paddingLeft: 0 }}>
            Clear opportunity filter
          </Button>
        </Card>
      ) : null}

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Estimations"
              value={totalEstimations}
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Draft Estimations"
              value={draftEstimations}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Approved Estimations"
              value={approvedEstimations}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Value"
              value={totalValue}
              prefix={<DollarOutlined />}
              precision={2}
              suffix={baseCurrency?.currency_code}
            />
          </Card>
        </Col>
      </Row>

      {/* Accuracy Metrics */}
      {accuracyMetrics && Object.keys(accuracyMetrics).length > 0 && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card title="Estimation Accuracy" size="small">
              <Statistic
                value={accuracyMetrics.accuracyRate || 0}
                suffix="%"
                valueStyle={{ color: '#3f8600' }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                {accuracyMetrics.completedEstimations || 0} completed estimations analyzed
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Avg Variance" size="small">
              <Statistic
                value={accuracyMetrics.averageVariancePercentage || 0}
                suffix="%"
                precision={1}
                valueStyle={{ color: '#cf1322' }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                Over vs Under: {accuracyMetrics.overestimationPercentage?.toFixed(0) || 0}% vs{' '}
                {accuracyMetrics.underestimationPercentage?.toFixed(0) || 0}%
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Trend" size="small">
              <Statistic
                value={accuracyMetrics.trend?.improvementRate || 0}
                suffix="%"
                prefix={accuracyMetrics.trend?.isImproving ? '↑' : '↓'}
                valueStyle={{ color: accuracyMetrics.trend?.isImproving ? '#3f8600' : '#cf1322' }}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                {accuracyMetrics.trend?.isImproving ? 'Improving' : 'Declining'} over last 90 days
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs for different views */}
      <Card>
        <Tabs defaultActiveKey="estimations">
          <TabPane tab="All Estimations" key="estimations">
            {/* Toolbar */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <Input
                  placeholder="Search estimations..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                />
                <Select
                  placeholder="Filter by status"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">All Status</Option>
                  <Option value="Draft">Draft</Option>
                  <Option value="Submitted">Submitted</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Rejected">Rejected</Option>
                </Select>
              </Space>
              <Space>
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  onClick={() => navigate('/estimation/ai-predict')}
                >
                  AI Cost Predictor
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalVisible(true)}
                >
                  New Estimation
                </Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={estimations}
              rowKey="id"
              loading={isLoading}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} estimations`
              }}
            />
          </TabPane>

          <TabPane tab="Quick Actions" key="quick-actions">
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={6}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => navigate('/estimation/create')}
                >
                  <PlusOutlined style={{ fontSize: 48, color: '#145A54' }} />
                  <h3>Single-Item Estimation</h3>
                  <p>Build estimation with full BOM and operations</p>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => {
                    // For now, show message to create estimation first
                    message.info('Create an estimation first, then access Multi-Item builder');
                  }}
                >
                  <BarChartOutlined style={{ fontSize: 48, color: '#722ed1' }} />
                  <h3>Multi-Item Estimation</h3>
                  <p>Multiple products with Simple/Detailed costing</p>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => navigate('/estimation/ai-predict')}
                >
                  <RobotOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                  <h3>AI Cost Prediction</h3>
                  <p>Quick cost estimate using machine learning</p>
                </Card>
              </Col>
              <Col span={6}>
                <Card
                  hoverable
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => navigate('/estimation/historical-analysis')}
                >
                  <BarChartOutlined style={{ fontSize: 48, color: '#faad14' }} />
                  <h3>Historical Analysis</h3>
                  <p>View cost trends and variance reports</p>
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Card>

      {/* Create Estimation Modal */}
      <Modal
        title="Create New Estimation"
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={createEstimationMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateEstimation}>
          <Form.Item
            name="estimation_name"
            label="Estimation Name"
            rules={[{ required: true, message: 'Please enter estimation name' }]}
          >
            <Input placeholder="Enter estimation name" />
          </Form.Item>
          <Form.Item
            name="customer_name"
            label="Customer Name"
            rules={[{ required: true, message: 'Please select customer' }]}
          >
            <Select
              showSearch
              placeholder="Select customer"
              loading={isLoadingCustomers}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.label ?? option?.children ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {customers?.map((customer) => (
                <Option key={customer.id} value={customer.customer_name}>
                  {customer.customer_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="project_name" label="Project Name">
            <Input placeholder="Enter project name (optional)" />
          </Form.Item>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item
            name="bom_id"
            label="BOM"
            rules={[{ required: true, message: 'Please select BOM' }]}
          >
            <Select placeholder="Select BOM">
              {/* Would fetch BOMs from API */}
              <Option value="bom-1">BOM-001 - Sample Product</Option>
              <Option value="bom-2">BOM-002 - Another Product</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[{ required: true, message: 'Please enter quantity' }]}
          >
            <Input type="number" placeholder="Enter quantity" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EstimationDashboardPage;

